/**
 * UiMessage — how screens hold a user-facing status/error in React state.
 *
 * Frontend-owned messages are stored as a translation KEY (+ params) so an
 * already-visible message re-renders in the new language on a switch.
 * Backend errors that carry a STABLE code (Localization Phase 12, Slice C1)
 * are stored as `kind: 'code'` — code + structured params + the backend's
 * legacy English prose as fallback — and are translated only at the render
 * boundary through the explicit allowlist below. Backend prose WITHOUT a
 * code is stored RAW and displayed verbatim (English) — the compatibility
 * fallback for old/unmigrated endpoints. Never a Spanish string-match table.
 */
import { t as globalT } from './index';
import { ApiRequestError } from '../network/ApiClient';

/**
 * Structural shape every namespace-bound `t` satisfies (i18next brands
 * TFunction per namespace, so a nominal TFunction type would reject a
 * 'play'-bound t). Parameters are `never` on purpose: any narrower key/
 * option types are assignable to it.
 */
type AnyTranslate = (key: never, options?: never) => unknown;

export type UiMessage =
  | { kind: 'key'; key: string; params?: Record<string, unknown> }
  | { kind: 'raw'; text: string }
  | { kind: 'code'; code: string; params?: Record<string, string | number>; fallback?: string };

/**
 * The EXPLICIT typed allowlist mapping supported backend codes to catalog
 * keys. Backend codes are never used directly as i18next keys: an unknown
 * future code simply is not in this map and renders its legacy English
 * fallback — a raw translation key can never reach the screen.
 *
 * Slice B1: session/lobby lifecycle (28 codes); Slice B2: active-gameplay
 * actions (17 codes); Slice B3a: social/messaging (16 codes); Slice B3b:
 * profile/identity (9 codes) — all pinned by the backend contract specs.
 * Later slices append here.
 */
export const API_ERROR_KEYS: Readonly<Record<string, string>> = Object.freeze({
  session_not_found:          'common:apiError.session_not_found',
  not_in_session:             'common:apiError.not_in_session',
  not_seated:                 'common:apiError.not_seated',
  already_in_session:         'common:apiError.already_in_session',
  session_create_conflict:    'common:apiError.session_create_conflict',
  session_full:               'common:apiError.session_full',
  session_closed:             'common:apiError.session_closed',
  session_not_invitable:      'common:apiError.session_not_invitable',
  join_failed:                'common:apiError.join_failed',
  not_host:                   'common:apiError.not_host',
  not_startable:              'common:apiError.not_startable',
  invalid_seat:               'common:apiError.invalid_seat',
  seat_taken:                 'common:apiError.seat_taken',
  same_seat:                  'common:apiError.same_seat',
  seats_locked:               'common:apiError.seats_locked',
  remove_requires_waiting:    'common:apiError.remove_requires_waiting',
  cannot_remove_self:         'common:apiError.cannot_remove_self',
  participant_not_found:      'common:apiError.participant_not_found',
  bot_requires_waiting:       'common:apiError.bot_requires_waiting',
  bot_before_first_game:      'common:apiError.bot_before_first_game',
  bot_position_occupied:      'common:apiError.bot_position_occupied',
  bot_pool_exhausted:         'common:apiError.bot_pool_exhausted',
  bot_difficulty_unavailable: 'common:apiError.bot_difficulty_unavailable',
  invitation_not_pending:     'common:apiError.invitation_not_pending',
  invitation_expired:         'common:apiError.invitation_expired',
  invitation_pending_exists:  'common:apiError.invitation_pending_exists',
  recipient_in_session:       'common:apiError.recipient_in_session',
  invalid_lobby_option:       'common:apiError.invalid_lobby_option',
  // Slice B2: active-gameplay actions (play/pass, next-round advance,
  // rematch vote, leave/close). Same contract: the code is a semantic id,
  // the catalog key is explicit, and anything not listed here falls back
  // to the backend's legacy English prose.
  game_not_found:             'common:apiError.game_not_found',
  game_not_active:            'common:apiError.game_not_active',
  not_your_turn:              'common:apiError.not_your_turn',
  tile_not_in_hand:           'common:apiError.tile_not_in_hand',
  must_open_double_six:       'common:apiError.must_open_double_six',
  tile_unplayable:            'common:apiError.tile_unplayable',
  must_pass:                  'common:apiError.must_pass',
  play_failed:                'common:apiError.play_failed',
  cannot_pass_with_move:      'common:apiError.cannot_pass_with_move',
  pass_failed:                'common:apiError.pass_failed',
  round_advance_failed:       'common:apiError.round_advance_failed',
  rematch_invalid_vote:       'common:apiError.rematch_invalid_vote',
  rematch_not_open:           'common:apiError.rematch_not_open',
  rematch_not_participant:    'common:apiError.rematch_not_participant',
  rematch_failed:             'common:apiError.rematch_failed',
  not_solo_game:              'common:apiError.not_solo_game',
  leave_failed:               'common:apiError.leave_failed',
  // Slice B3a: social/messaging — Friends and friend requests, direct
  // messages (including DM validation codes), and the remaining
  // social-semantics invitation errors. Same contract as above.
  // NOTE: the four dm_* VALIDATION codes deliberately map to the EN catalog
  // value "HTTP 422" — the character-identical legacy English UI output for
  // this branch (the old response had no `error` field, so the client
  // showed its own HTTP fallback). Spanish shows the meaningful sentence.
  user_not_found:             'common:apiError.user_not_found',
  friend_request_self:        'common:apiError.friend_request_self',
  friendship_exists:          'common:apiError.friendship_exists',
  // In-game/spectator "Add Friend" (game-context authorization) — new code,
  // no legacy prose to match; English/Spanish are both newly authored.
  friend_request_game_context_required: 'common:apiError.friend_request_game_context_required',
  friend_request_not_pending: 'common:apiError.friend_request_not_pending',
  friend_request_not_found:   'common:apiError.friend_request_not_found',
  recipient_not_found:        'common:apiError.recipient_not_found',
  dm_self:                    'common:apiError.dm_self',
  dm_friends_only:            'common:apiError.dm_friends_only',
  dm_content_required:        'common:apiError.dm_content_required',
  dm_body_too_long:           'common:apiError.dm_body_too_long',
  dm_image_type:              'common:apiError.dm_image_type',
  dm_image_size:              'common:apiError.dm_image_size',
  invitation_self:            'common:apiError.invitation_self',
  invitation_friends_only:    'common:apiError.invitation_friends_only',
  invitation_cancel_pending_only: 'common:apiError.invitation_cancel_pending_only',
  invitation_not_found:       'common:apiError.invitation_not_found',
  // Slice B3b: profile / identity (username, country, avatar, table and tile
  // skin). These endpoints publish the ORIGINAL dual shape and the client
  // still discriminates on `error`; the additive `code` is what maps here.
  // NOTE: the username_too_short/too_long/charset EN values are the EXACT
  // ActiveModel fragments shown today — English is preserved verbatim, and
  // Spanish gets a complete sentence.
  invalid_country_code:       'common:apiError.invalid_country_code',
  invalid_avatar_key:         'common:apiError.invalid_avatar_key',
  invalid_table_skin:         'common:apiError.invalid_table_skin',
  invalid_tile_skin:          'common:apiError.invalid_tile_skin',
  username_taken:             'common:apiError.username_taken',
  username_too_short:         'common:apiError.username_too_short',
  username_too_long:          'common:apiError.username_too_long',
  username_charset:           'common:apiError.username_charset',
  username_invalid:           'common:apiError.username_invalid',
  // Slice C1 (closure): the locale-preference rejection, the shared
  // plain-membership/spectator 401 family, the four distinct invitation/
  // friendship ownership 403 meanings (kept separate — never merged), and
  // push validation. English values are the EXACT legacy prose/status-word
  // text this app already showed for these paths (most were previously
  // silent or shown only via the raw backend-prose fallback) — no English
  // wording changes; only Spanish is new.
  invalid_locale:                     'common:apiError.invalid_locale',
  session_membership_required:        'common:apiError.session_membership_required',
  spectate_not_allowed:               'common:apiError.spectate_not_allowed',
  invitation_recipient_required:      'common:apiError.invitation_recipient_required',
  invitation_sender_required:         'common:apiError.invitation_sender_required',
  friend_request_recipient_required:  'common:apiError.friend_request_recipient_required',
  friend_request_sender_required:     'common:apiError.friend_request_sender_required',
  invalid_token:                      'common:apiError.invalid_token',
  invalid_platform:                   'common:apiError.invalid_platform',
  push_token_conflict:                'common:apiError.push_token_conflict',
  // Slice D1: account deletion. In practice unreachable through the live
  // UI — a deleted account fails authentication before this code could ever
  // be returned (see ApplicationController#current_user's deleted_at
  // guard) — mapped anyway for defense in depth / direct API callers.
  account_already_deleted:            'common:apiError.account_already_deleted',
});

/**
 * Backend params are STABLE ids, never prose. Where an id has a per-language
 * sentence value, a per-code adapter maps it at RENDER time through an
 * explicit typed table of narrowly scoped sentence-parameter keys (English
 * keeps the legacy lowercase ids verbatim; Spanish uses the localized
 * difficulty names). A key is never built from an arbitrary backend string
 * and the stored UiMessage params are never mutated — only a copied
 * render-time object is adapted.
 *
 * An adapter returns `null` to signal "unsupported parameter value": the
 * renderer then shows the backend's legacy English fallback verbatim (or the
 * shared localized generic when no fallback exists) instead of interpolating
 * an internal enum into otherwise-Spanish copy. Never a raw key, never a
 * crash, never HTML.
 */
const DIFFICULTY_PARAM_KEYS: Readonly<Record<string, string>> = Object.freeze({
  easy:   'common:apiErrorParam.difficulty.easy',
  medium: 'common:apiErrorParam.difficulty.medium',
  hard:   'common:apiErrorParam.difficulty.hard',
});

type ApiErrorParams = Record<string, string | number>;

const PARAM_ADAPTERS: Readonly<Record<string, (params: ApiErrorParams) => ApiErrorParams | null>> = Object.freeze({
  bot_pool_exhausted: (params) => {
    const id = params.difficulty;
    const labelKey = typeof id === 'string' ? DIFFICULTY_PARAM_KEYS[id] : undefined;
    if (!labelKey) return null; // unknown/missing/non-string id → use fallback
    return { ...params, difficulty: String(globalT(labelKey as never)) };
  },
});

/** Returns the render-time params, or `null` when a code's adapter rejected
 *  the supplied value (caller must fall back to the legacy prose). */
function adaptParams(code: string, params?: ApiErrorParams): ApiErrorParams | undefined | null {
  const adapter = PARAM_ADAPTERS[code];
  if (!adapter) return params;
  return adapter(params ?? {});
}

export function uiKey(key: string, params?: Record<string, unknown>): UiMessage {
  return { kind: 'key', key, params };
}

/**
 * Build the UiMessage for a thrown request error:
 *   coded ApiRequestError → 'code' (semantic code + params + prose fallback);
 *   uncoded Error with prose → 'raw' (legacy verbatim English);
 *   anything else → the frontend-owned fallback key.
 * Nothing here translates — the semantic value goes into state, and the
 * render boundary resolves the active language.
 */
export function uiFromError(e: unknown, fallbackKey: string): UiMessage {
  if (e instanceof ApiRequestError && e.code) {
    return { kind: 'code', code: e.code, params: e.params, fallback: e.message || undefined };
  }
  const text = e instanceof Error ? e.message : '';
  return text ? { kind: 'raw', text } : uiKey(fallbackKey);
}

export function renderUiMessage(t: AnyTranslate, m: UiMessage | null): string {
  if (!m) return '';
  if (m.kind === 'raw') return m.text;
  if (m.kind === 'code') {
    const key = API_ERROR_KEYS[m.code];
    if (key) {
      // Params interpolate as plain text (escapeValue:false + React escaping
      // — user values like usernames render literally, never as markup).
      // Stable-id params (e.g. difficulty) are localized here, at render
      // time, via the typed adapter table above; an adapter rejecting the
      // value routes to the legacy fallback below.
      const params = adaptParams(m.code, m.params);
      if (params !== null) {
        return String(globalT(key as never, params as never));
      }
    }
    // Unknown future code — or a known code whose parameter value the
    // adapter rejected: the backend's legacy English prose; if even that is
    // missing, the shared localized generic error.
    return m.fallback ?? String(globalT('common:modal.errorGeneric' as never));
  }
  // Keys are validated at the call sites that construct them; the helper is
  // generic across namespaces, so the typed-key check is relaxed here.
  return String(t(m.key as never, m.params as never));
}
